import React, { Fragment, useContext, useEffect, useState } from "react";
import { Button, Modal, Row, Col, Card, Icon, Tooltip, Divider } from "antd";
import I18n from "../../common/I18n";
import { withRouter } from "react-router-dom";

import { OrderConfigContext } from "../../context/OrderConfigContext";
import MoveTypeBadge from "../common/MoveTypeBadge";
import { FormTemplateIcon, getColorForTitleCreateOrderScreen, getOrderTypeIcon, renderNotification } from "./OrderTypeIcons";
import { CircledCloseIcon,InfoIconSvg } from "../../common/CustomIcon";
import DownArrowIcon from "../../../assets/images/DownArrow.svg";
import { OrgContext } from "../../context/OrgContext";
import { UserContext } from "../../context/UserContext";
import "./TooltipCard.scss";
import { convertToArray, formatText } from "../../helpers/common";
import { catalog as fetchMoveTypeCatalog } from "../../api/ServiceLevels";

const CreateOrderButton = (props) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [hoveredCode, setHoveredCode] = useState(null);
  // Active OrganizationMoveType set for this org — the source of truth for which
  // order types the popup offers. `null` = not loaded / unavailable (fall back
  // to the legacy order_types CSV); a non-empty Set = show only these codes so
  // deactivated move types disappear from Mode B immediately.
  const [activeMoveTypeCodes, setActiveMoveTypeCodes] = useState(null);
  // P4 DB-driven tiles: full tile objects built from the catalog (alias,
  // description, tooltips, badge). null = not loaded / flag off => the
  // enhancedOrderTypes-based paths below drive the popup.
  const [dbTiles, setDbTiles] = useState(null);
  const { orderConfig } = useContext(OrderConfigContext);
  const { orgSettings : organizationSettings, currentOrg  }= useContext(OrgContext);
  // Same org flag that DB-drives the create form's getOrderTypeConfig (P3) —
  // tiles and form must flip together, or the popup could offer a type the
  // form cannot resolve.
  const dbOrderTypesEnabled = organizationSettings?.service_level_flow_enabled === "true";

  useEffect(() => {
    let cancelled = false;
    fetchMoveTypeCatalog({ orgId: currentOrg?.id })
      .then((res) => {
        if (cancelled) return;
        if (res?.success) {
          const codes = new Set();
          (res.data || []).forEach((omt) => {
            const mt = omt?.move_type || {};
            if (mt.order_label) codes.add(mt.order_label);
            if (mt.code) codes.add(mt.code);
          });
          setActiveMoveTypeCodes(codes);
          // P4: tiles straight from the catalog (already active-only, ordered by
          // display_order). No extra_code — retired; variants are move types.
          const tiles = (res.data || [])
            .map((omt) => ({
              code: omt.move_type?.order_label,
              type_of_order: omt.move_type?.base_order_type,
              label: omt.alias_name,
              // OMT description/tooltip are nullable (migrated OMTs predate the
              // display-text seed); default to safe strings so tile rendering
              // (formatText, tooltip label) never dereferences null.
              description: omt.description || "",
              toolTipLabel: omt.tooltip_label || "",
              message: omt.tooltip_message || [],
              short_code: omt.short_code,
              display_color: omt.display_color,
              dbDriven: true,
            }))
            .filter((t) => t.code && t.type_of_order);
          setDbTiles(tiles);
        }
      })
      .catch(() => {
        // Leave as null so the legacy CSV fallback drives the popup.
        if (!cancelled) {
          setActiveMoveTypeCodes(null);
          setDbTiles(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [currentOrg?.id]);

  const { orgRules } = useContext(UserContext);
  const isCLIEnabled = orgRules?.org_billing_rules?.cli_order_types === 'true';
  const { viewType = "BUTTON",showOnlyArrow, typeOFOrder = "D", handleTypeChange, reqFrom = "orders" ,
    disabledCodes = [], showOptions = true, serviceLevelSelected = false} = props;
  const resolvedTypeOFOrder = typeOFOrder;
  // Mode A (Account -> Service Level) entry flow. Flag-gated, defaults OFF for
  // every org today (OrganizationConfig `service_level_flow_enabled`, default
  // "false" — see app/modules/organizations.rb). Scoped to the top-level
  // "Create Order" button entry point only (viewType === "BUTTON"); the
  // inline "change order type" surface used from within CreateOrderScreen
  // (viewType === "label") keeps the original tile modal even when the flag
  // is on, since Mode A's account-first flow doesn't compose with an
  // in-progress order form.
  const isServiceLevelFlowEnabled =
    viewType === "BUTTON" && organizationSettings?.service_level_flow_enabled === "true";
  // Mode A on the create form's heading (viewType "label"): the inline Service
  // Level row owns the order type, so the heading hides the type name + the
  // order-type dropdown and never opens the tile popup. Flag off => unchanged.
  const isModeAInlineForm =
    viewType !== "BUTTON" && organizationSettings?.service_level_flow_enabled === "true";
  const showModal = () => {
    // Phase 4.3 Mode A: no order-type popup — navigate straight to the
    // account-first inline create form.
    if (isServiceLevelFlowEnabled) {
      props.history.push({ pathname: `/orders/create` });
      return;
    }
    setIsModalVisible(true);
  };
  

const excludedOrderTypesRaw = convertToArray(organizationSettings.order_types) || [];
// Order types controlled by the linehaul-plus flag
const linehaulPlusTypes = ["PLH", "LHD", "PLHD","NLH"];

const excludedOrderTypes = organizationSettings?.enable_linehaul_plus === "true" ? excludedOrderTypesRaw : excludedOrderTypesRaw.filter(code => !linehaulPlusTypes.includes(code));

// Legacy source of truth: the order_types CSV. Used only as a fallback for orgs
// that have not been migrated to OrganizationMoveType (no active OMTs yet).
const legacyOrdersList = excludedOrderTypes.length
  ? orderConfig.enhancedOrderTypes.filter(
      (order) => order.extraCode
        ? excludedOrderTypes.includes(order.extraCode)
        : excludedOrderTypes.includes(order.code)
    )
  : orderConfig.enhancedOrderTypes.filter((order) => !order.extraCode);

let ordersList;
if (dbOrderTypesEnabled && dbTiles && dbTiles.length > 0) {
  // P4: fully DB-driven tiles — the catalog supplies the list AND each tile's
  // display (alias/description/tooltips/badge). enhancedOrderTypes untouched;
  // the linehaul-plus flag still gates its types (by order_label).
  ordersList = organizationSettings?.enable_linehaul_plus === "true"
    ? dbTiles
    : dbTiles.filter((t) => !linehaulPlusTypes.includes(t.code));
} else if (activeMoveTypeCodes && activeMoveTypeCodes.size > 0) {
  // Primary path: render exactly the org's ACTIVE move types, so deactivating a
  // move type (config screen, unassign, or super-admin order-type removal) hides
  // its tile here immediately. The linehaul-plus flag still gates its types.
  // Also keep any legacy "orphan" code — one present in the org's order_types CSV
  // but backed by no master MoveType (e.g. B/E/H — Beyond/Exclusive/Hotshot,
  // frontend-only tiles). Without this union they'd be dropped here (they never
  // become active OMTs) and the CSV fallback below is unreachable once any OMT
  // exists, so legacy orgs would lose those tiles. Removed per-org when the org
  // migrates to Service Levels (its CSV drops B/E/H at migration time).
  ordersList = orderConfig.enhancedOrderTypes.filter(
    (order) =>
      activeMoveTypeCodes.has(order.code) ||
      activeMoveTypeCodes.has(order.extraCode) ||
      excludedOrderTypes.includes(order.code) ||
      (order.extraCode && excludedOrderTypes.includes(order.extraCode))
  );
  if (organizationSettings?.enable_linehaul_plus !== "true") {
    ordersList = ordersList.filter(
      (order) => !linehaulPlusTypes.includes(order.code) && !linehaulPlusTypes.includes(order.extraCode)
    );
  }
} else {
  ordersList = legacyOrdersList;
}


  const handleOk = () => {
    // Handle the OK button click event
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    // Handle the Cancel button click event
    setIsModalVisible(false);
  };

  const labelsToIgnore = ["SDR", "SDD", "MDR", "MDD"]

  const handleAddOrder = (orderType, label_code, extraCode) => {
    handleCancel()
    if(viewType === "BUTTON") {
      props.history.push({
        pathname: `/orders/create`,
        orderType,
        label_code,
        extraCode,
      });
    } else{
      handleCancel()
      handleTypeChange(orderType, label_code, extraCode)
    }
    
  };
  const getIconProps = (iconProps) => {
    switch (iconProps) {
      case "R":
      case "SDR":
        return { path2Color: "#fff", width: 24, height: 24 };
      case "MDR":
        return { color2: "#fff", width: 24, height: 24 };
      case "D":
      case "SDD":
        return { color2: "#fff", width: 24, height: 24 };
      case "T":
        return { color3: "#fff", width: 24, height: 24 };
      case "M":
        return { color3: "#FFf", width: 24, height: 24 };
      case "MS":
        return { color2: "#FFf", width: 24, height: 24 };
      case "LH":
        return { color2: "#fff", width: 24, height: 24 };
      case "RD":
        return { color:"#2DB5FF80", color1:"#2DB5FF", color2: "#fff", width: 24, height: 24, rotate: 180 };
      case "RT":
        return { color :"#DC2DFF80", color1 : "#DC2DFF", color2: "#fff", width: 24, height: 24, rotate: 180 };
     case "OBT":
      return {color2 : "#FFF", width:"24",height:"24" };
      case "CDK":
      case "WO":
        return {color2 : "#FFF", width:"24",height:"24" };
      case "PCDK":
        return {color3 : "#FFF", width:"24",height:"24" };
      case "CDKD":
        return {color3 : "#FFF", width:"24",height:"24" };
      default:
        return { path2Color: "#fff", width: 24, height: 24 };
    }
  };
  

  const TooltipCard = ({ label, icon, message }) => (
    <div
      style={{
        backgroundColor: "#1D2540",
        borderRadius: "8px",
        overflow: "hidden",
        width: "256px",
        padding: "0",
      }}
    >
      <Card
        bordered={false}
        title={<span className="break-word">{label}</span>}
        extra={icon}
        headStyle={{
          borderBottom: "none",
          backgroundColor: "#1D2540",
          padding: "16px 16px 8px 16px",
          color: "white",
        }}
        bodyStyle={{
          backgroundColor: "#1D2540",
          color: "white",
          padding: "0px 16px 16px 16px",
        }}
      >
        <div>
          {message.map((para, idx) => (
            <p key={idx} className="body-2-large-Tooltip" style={{ marginBottom: "12px" }}>
              {para}
            </p>
          ))}
        </div>
      </Card>
    </div>
  );
  // DB-driven order types: the heading's badge + name come from the catalog tile
  // (OMT short_code / display_color / alias_name), mirroring the popup tiles.
  // null when the flag is off, the catalog hasn't loaded, or the type has no
  // tile (legacy extraCode variants) => every legacy path renders byte-identically.
  const dbHeadingTile = dbOrderTypesEnabled && dbTiles
    ? dbTiles.find((t) => t.code === resolvedTypeOFOrder)
    : null;
  return (
    <Fragment>
      {viewType === "BUTTON" ? (
        <Button
          data-testid="create-order-screen-button"
          type="primary"
          onClick={showModal}
          style={{ marginTop: "2px", marginLeft: 10 }}
        >
          {I18n.t("order.create")}
        </Button>
      ) : showOnlyArrow ? (
        <Row style={{ display: "flex" }}>
          
            <Col>
              {/* Mode A (service_level_flow_enabled): the Service Level row owns the
                  order type, so on the edit heading show the type NAME as static
                  text — no click-to-change and no dropdown arrow below. */}
              <span onClick={(e) => (showOptions && !isModeAInlineForm) ? showModal(e) : undefined}>
                {dbHeadingTile ? (
                  <span className="create-order-heading" style={{ fontSize: "14px", lineHeight: "14px", color: dbHeadingTile.display_color }}>
                    {dbHeadingTile.label}
                  </span>
                ) : (
                  getColorForTitleCreateOrderScreen(resolvedTypeOFOrder, "14px", "14px", currentOrg?.code, isCLIEnabled)
                )}
              </span>
            </Col>
            {showOptions && !isModeAInlineForm && (
              <Col onClick={showModal} style={{ cursor: "pointer" }}>
                {/* <Icon type="caret-down" /> */}
                <img
                  style={{ height: "25px", width: "25px", marginTop: "0px" }}
                  className="down-arrow-icon"
                  src={DownArrowIcon}
                  alt=""
                />
              </Col>
            )}
          </Row>
      ) : (
        <Row type="flex" onClick={showOptions && !isModeAInlineForm ? showModal : undefined} gutter={8}>
          {/* Mode A (service_level_flow_enabled): the Service Level row owns the
              order type. Until a service level is selected there is no move type
              to show, so the create heading hides the default badge. Once an SL
              is selected (serviceLevelSelected), the resolved move type's badge
              reappears. Flag off => always shown (unchanged legacy path). */}
          {(!isModeAInlineForm || serviceLevelSelected) && (
            <Col>
              {dbHeadingTile ? (
                <MoveTypeBadge
                  shortCode={dbHeadingTile.short_code}
                  color={dbHeadingTile.display_color}
                  title={dbHeadingTile.label}
                  size={40}
                />
              ) : (
                getOrderTypeIcon(resolvedTypeOFOrder, {
                  height: "40",
                  width: "40",
                })
              )}
            </Col>
          )}
          <Col className="center">
            <span className="create-order-heading-text">
              {/* Mode A (service_level_flow_enabled): the Service Level row owns
                  the order type, so the heading is just "Create Order" — no type
                  name and no order-type popup (label + dropdown hidden below). */}
              Create {isModeAInlineForm ? null : dbHeadingTile ? (
                <span className="create-order-heading" style={{ color: dbHeadingTile.display_color }}>
                  {dbHeadingTile.label}
                </span>
              ) : (
                getColorForTitleCreateOrderScreen(resolvedTypeOFOrder, null, null, currentOrg?.code, isCLIEnabled)
              )} Order
            </span>
          </Col>
          {showOptions && !isModeAInlineForm && <Col>
                {/* <Icon type="caret-down" /> */}
               <img  className="down-arrow-icon"src={DownArrowIcon} alt="" />
           </Col>}
        </Row>
      )}

      <Modal
        title={
          <div className="modal-header">
            <span style={{fontSize:"23px", fontWeight:"bold"}}>Select Order Type</span>
              <Tooltip title="Close">
            <div className="custom-close-icon-wrapper" style={{ display: "inline-block" }}>
                <CircledCloseIcon
                color="#576EC1"
                width="55px"
                height="55px"
                onClick={handleCancel}
                style={{ cursor: "pointer" }}
              />
            </div>
              </Tooltip>
          </div>
        }
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        width={1024}
        footer={null}
        className="custom-modal"
        closable={false}
        data-testid="orderType-modal"
      >
        <Divider style={{ margin: "10px 0" }} />
        <Row gutter={[16, 16]} data-testid="ordertypes">
          {ordersList.map((orderType) => {
            const isClickable = !disabledCodes.includes('ALL') && !disabledCodes.includes(orderType.code)
            return (
            labelsToIgnore.includes(orderType.code) ? null : 
            <Col span={8} data-testid={`ordertype-select-${orderType.label}`}>
              <Card
                className={`order-type-card ${ !isClickable ? 'not_allowed': ''}`}
                onClick={() => 
                  isClickable ? handleAddOrder(orderType.type_of_order , orderType.code, orderType.extraCode) :
                  renderNotification('Action Not Allowed', 'error')
                }
                 onMouseEnter={(e) => {
                  e.currentTarget.querySelector(".info-icon").style.display = "block";
                  setHoveredCode(orderType.code);
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.querySelector(".info-icon").style.display = "none";
                  setHoveredCode(null);
                }}
                    style={{
                      position: "relative",
                      ...(hoveredCode === orderType.code && isClickable && {
                        B: { backgroundColor: "#EEF2FF", borderColor: "#C7D2FE", borderStyle: "solid", borderWidth: "1px" },
                        E: { backgroundColor: "#FFF7E8", borderColor: "#FCD34D", borderStyle: "solid", borderWidth: "1px" },
                        H: { backgroundColor: "#FEF2F2", borderColor: "#FCA5A5", borderStyle: "solid", borderWidth: "1px" },
                      }[orderType.code])
                    }}
                  >
                    <Tooltip
                    overlayClassName="ToolTipCard"
                      title={
                        <TooltipCard
                          label={`${orderType.toolTipLabel || orderType.label}`}
                          icon={orderType.dbDriven
                            ? <MoveTypeBadge shortCode={orderType.short_code} color={orderType.display_color} title={orderType.label} size={24} />
                            : getOrderTypeIcon(orderType.extraCode || orderType.code, getIconProps(orderType.extraCode || orderType.code))}
                          message={orderType.message || []}
                        />
                      }
                    >
                {/* <div style={{ display: "inline-block" }}> */}
                      <div
                        style={{
                          position: 'absolute',
                          top: '10px',
                          right: '30px',
                          cursor: 'pointer',
                          zIndex: 1,
                        }}
                      >
              <InfoIconSvg
                styling={{
                  position: "absolute",
                  color:"#576ec1",
                  // top: "10px",
                  // right: "10px",
                  cursor: "pointer",
                  zIndex: 1,
                  display: "none",
                }}
                className="info-icon"
              />
              </div>
            </Tooltip>
               <Row gutter={8} style={{ height: "100%" }}>
            <Col span={24}>
              <div
                style={{
                  display: "flex",
                  alignItems: "start",
                  gap: "8px",
                  paddingBottom:"10px"
                }}
              >
               <div style={{ display: "flex", alignItems: "center" }}>
                 {orderType.dbDriven
                  ? <MoveTypeBadge shortCode={orderType.short_code} color={orderType.display_color} title={orderType.label} size={25} />
                  : getOrderTypeIcon(orderType.extraCode || orderType.code, {
                      width: 25,
                      height: 25,
                    })}
                </div>

                <span className="order-type-label">
                  {orderType.label}
                </span>
              </div>
            </Col>

            <Col span={24}>
              {/* {orderType.subtext && (
                <p className="order-type-subtext">{orderType.subtext}</p>
              )} */}

              <p className="order-type-description">
                {formatText(orderType.description || "")}
              </p>
            </Col>
          </Row>
              </Card>
            </Col>
          )})}
          {reqFrom === "create" ? null : (
            <Col span={8} data-testid="template-order">
              <Card
                className="order-type-card"
                onClick={() => props.setIsFormTemplate(true)}
                onMouseEnter={(e) => {
                  e.stopPropagation()
                  e.currentTarget.querySelector(".info-icon").style.display = "block";
                }}
                onMouseLeave={(e) => {
                  e.stopPropagation()
                  e.currentTarget.querySelector(".info-icon").style.display = "none";
                }}
                style={{ position: "relative" }} 
              >
                <Tooltip
                 overlayClassName="ToolTipCard"
                  title={
                    <TooltipCard
                      label={`Templates`}
                      icon={<FormTemplateIcon width="24" height="24" color="#fff" />}
                      message={["Saved Templates allow you to streamline order creation by saving frequently used order details for future use.",
                        " In order to create a template, check the Save as Template box when creating a new order. These templates can then be selected in the order creation dialog, enabling you to quickly generate similar orders without re-entering repetitive information."]}
                    />
                  }
                  placement="right"
                  overlayStyle={{ background: "transparent", boxShadow: "none" }}
                  overlayInnerStyle={{ background: "transparent", padding: 0 }}
                  arrow={false}
                  >
                 <div
                        style={{
                          position: 'absolute',
                          top: '10px',
                          right: '30px',
                          cursor: 'pointer',
                          zIndex: 1,
                        }}
                      >
                      <InfoIconSvg
                        styling={{
                          position: "absolute",
                          color: "#576ec1",
                          cursor: "pointer",
                          zIndex: 1,
                          display: "none",
                        }}
                        className="info-icon"
                      />
                      </div>
                    </Tooltip>
              
              <Row align="middle" style={{ height: "100%" }}>
                  <Col
                    span={6}
                    style={{ display: "flex", justifyContent: "center" }}
                  >
                   
                    <FormTemplateIcon />
                  </Col>
                  <Col
                    span={18}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      gap: "4px",
                    }}
                  >
                    <span className="order-type-label">From Template</span>
                    <p className="order-type-description">
                      Start a new order from a previously saved recurring
                      order.
                    </p>
                  </Col>
                </Row>
              </Card>
            </Col>
          )}
        </Row>
      </Modal>
    </Fragment>
  );
};

export default withRouter(CreateOrderButton);
